import { useEffect, useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { getBlogPosts } from '../lib/blog';
import HeroCarousel from '../components/HeroCarousel';
import { BlogCard } from '../components/BlogCard';
import ComparisonTableHomepage from '../components/ComparisonTableHomepage';
import ComparisonCardHomepage from '../components/ComparisonCardHomepage';
import { getBestDiscount } from '../utils/getBestDiscount';

// Simple hook for mobile check
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

import { loadAllPropFirmData } from '../data/propFirmData';
import type { BlogPost, PropFirm } from '../types';
import { CardSkeleton, TableSkeleton } from '../components/LoadingSkeleton';
import HeroVideo from '../components/HeroVideo';

export function HomePage() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [firmData, setFirmData] = useState<Record<string, PropFirm>>({});
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  // --- Data prep for comparison section (shared for both table and cards) ---
  const sections: (1|2)[] = [1, 2];
  const firms = Object.values(firmData).filter(firm => firm.id !== 'blueguardian');
  const getRows = (type: 1 | 2) => firms
    .map(firm => {
      const match = (firm.pricing || []).filter(p => {
        const isRapid = p.label?.toLowerCase().includes('rapid');
        if (type === 1 && (p.phases !== 1 || isRapid)) return false;
        if (type === 2 && p.phases !== 2) return false;
        if (p.accountSize !== 100000) return false;
        const isPrimeCFD = firm.id === 'ttp' && p.name?.toLowerCase().includes('prime cfd');
        if (firm.id === 'ttp' && !isPrimeCFD) return false;
        const isSeacrestTwoStep = firm.id === 'seacrest-funded' && p.name?.toLowerCase().includes('two-step challenge');
        if (firm.id === 'seacrest-funded' && !isSeacrestTwoStep) return false;
        return true;
      });
      if (!match.length) return null;
      const best = match.reduce((prev, curr) => curr.fee < prev.fee ? curr : prev);
      // Discount logic (reuse from ComparisonTableHomepage)
      const discount = getBestDiscount(firm.discounts ?? [], best);
      const hasDiscount = discount?.code && discount?.percent;
      return {
        ...firm,
        fee: best.fee,
        discountedFee: hasDiscount && discount.percent != null ? Math.round(best.fee * (1 - discount.percent / 100)) : best.fee,
        accountSize: best.accountSize,
        maxDD: typeof best.maxDrawdownPercent === 'number' ? best.maxDrawdownPercent : (typeof best.maxDrawdown === 'number' ? best.maxDrawdown : '-'),
        dailyDD: typeof best.dailyDrawdownPercent === 'number' ? best.dailyDrawdownPercent : '-',
        leverage: best.leverage || '-',
        isBestValue: false,
        discounts: discount ? [discount] : [],
        affiliateLink: firm.affiliateLink,
        profitSplit: typeof best.profitSplit === 'number' && !isNaN(best.profitSplit) ? `${best.profitSplit}%` : '-',
      };
    })
    .filter((row): row is NonNullable<typeof row> => !!row)
    .sort((a, b) => (a?.fee ?? 0) - (b?.fee ?? 0))
    .slice(0, 5);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [posts, firms] = await Promise.all([
          getBlogPosts(),
          loadAllPropFirmData()
        ]);
        setBlogPosts(posts.slice(0, 3));
        setFirmData(firms);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>PropViper – Top Prop Firm Reviews & Trader Insights</title>
        <meta name="description" content="Discover the best prop trading firms, honest reviews, and expert trading tips. Compare firms and get funded faster with PropViper." />
        <link rel="canonical" href="https://www.propviper.com/" />
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden py-8 sm:py-12 md:py-16 lg:py-24">
        <Suspense fallback={
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/70" />
        }>
          <HeroVideo />
        </Suspense>

        <div className="relative z-10 container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-8 md:gap-10">
            <div className="text-center md:text-left md:w-1/2">
              <span className="text-xs sm:text-sm sm:text-base font-medium text-yellow-400 uppercase tracking-widest">
                Prop Firm Comparison
              </span>
              <div className="bg-white/10 backdrop-blur-md p-4 sm:p-6 md:p-8 rounded-xl text-white shadow-lg mt-3 sm:mt-4">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-yellow-400">
                  Get Funded by the Best <br className="hidden sm:block" /> Prop Trading Firms
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-yellow-600 mt-3 sm:mt-4">
                  Compare evaluations, find exclusive discounts, and read honest reviews before taking your next prop challenge.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4 sm:mt-6 justify-center md:justify-start">
                  <Link to="/compare" className="bg-yellow-400 text-black px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold hover:bg-yellow-300 transition text-center text-sm sm:text-base">
                    Compare Firms
                  </Link>
                  <Link to="/reviews" className="bg-white text-yellow-700 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold hover:bg-yellow-100 transition text-center text-sm sm:text-base">
                    Read Reviews
                  </Link>
                </div>
                <p className="text-xs sm:text-sm sm:text-base text-yellow-600 mt-4 sm:mt-6">Helping traders find the right prop firm</p>
              </div>
            </div>

            <div className="md:w-1/2 w-full flex flex-col items-center gap-3 sm:gap-4 mt-4 sm:mt-6 md:mt-0">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE SECTION */}
      <section className="py-12 sm:py-16 md:py-20 bg-gray-950 text-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-yellow-400">Top Prop Trading Firms Compared</h2>
            <p className="text-yellow-300 text-base sm:text-lg">
              We've analyzed the best prop trading firms to help you make the right choice.
            </p>
          </div>

          {isLoading ? (
            <div className="max-w-6xl mx-auto">
              <TableSkeleton rows={5} />
            </div>
          ) : (
            <>
              {/* Responsive: Table for desktop/tablet, Cards for mobile */}
              {!isMobile ? (
                <ComparisonTableHomepage firms={Object.values(firmData)} />
              ) : (
                <div className="space-y-10">
                  {sections.map(type => (
                    <div key={type}>
                      <h3 className="text-xl font-bold text-yellow-400 mb-3">{type}-Step Evaluation</h3>
                      <div className="space-y-4">
                        {getRows(type).map((row, idx) =>
                          row ? <ComparisonCardHomepage key={row.id + '-' + idx} firm={row} /> : null
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-12 sm:mt-16 text-center">
                <Link
                  to="/compare"
                  className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md px-4 sm:px-6 py-2 sm:py-3 rounded-full text-yellow-400 font-semibold shadow-md hover:bg-white/20 transition text-sm sm:text-base"
                >
                  View Full Comparison
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* BLOG SECTION */}
      <section className="py-12 sm:py-16 md:py-20 pb-4 bg-black text-yellow-100">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-yellow-400">Latest Insights</h2>
            <p className="text-yellow-300 text-base sm:text-lg">New trading strategies, prop firm breakdowns, and tips from funded traders.</p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
              {[1, 2, 3].map((i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : blogPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
                {blogPosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>

              <div className="text-center mb-8 sm:mb-12">
                <Link
                  to="/blog"
                  className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md px-4 sm:px-6 py-2 sm:py-3 rounded-full text-yellow-400 font-semibold shadow-md hover:bg-white/20 transition text-sm sm:text-base"
                >
                  View All Articles
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default HomePage;
